import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import {
    applyTypography,
    TYPOGRAPHY_VARIANTS,
} from '../../styled/theme/typography-utils';
import LytIcon from '../icon/LytIcon';
import { ICON_NAMES } from '../icon/icon-constants';

/**
 * Tag variants defining color schemes
 */
export const TAG_VARIANTS = {
    DEFAULT: 'default',
    RED: 'red',
    GREEN: 'green',
    BLUE: 'blue',
    YELLOW: 'yellow',
};

/**
 * Tag appearance options
 */
export const TAG_APPEARANCES = {
    DEFAULT: 'default',
    ROUNDED: 'rounded',
};
const TextContent = styled.span`
    flex: 1;
    word-break: break-word;
    hyphens: auto;
    line-height: 1.2;
`;

/**
 * Get colors for each variant
 */
const getVariantColors = (variant, theme) => {
    const colors = theme?.colors || {};
    const variantMap = {
        [TAG_VARIANTS.DEFAULT]: {
            background: {
                default: colors.Neutral2 || '#F0F2F9',
                hover: colors.Neutral3 || '#DDDFE5',
                pressed: colors.Neutral4 || '#CACCD2',
            },
            text: colors.Neutral11 || '#343436',
            border: colors.Blue4 || '#0063FF',
        },
        [TAG_VARIANTS.RED]: {
            background: {
                default: colors.Red0 || '#F9E2E1',
                hover: colors.Red1 || '#F3B1AF',
                pressed: colors.Red2 || '#EB7B77',
            },
            text: colors.Neutral11 || '#343436',
            border: colors.Blue4 || '#0063FF',
        },
        [TAG_VARIANTS.GREEN]: {
            background: {
                default: colors.Green0 || '#C6FFD9',
                hover: colors.Green1 || '#87FFBB',
                pressed: colors.Green2 || '#5CF5A7',
            },
            text: colors.Neutral11 || '#343436',
            border: colors.Blue4 || '#0063FF',
        },
        [TAG_VARIANTS.BLUE]: {
            background: {
                default: colors.Blue1 || '#D8E8FF',
                hover: colors.Blue2 || '#B6D2FF',
                pressed: colors.Blue3 || '#69A3FF',
            },
            text: colors.Neutral11 || '#343436',
            border: colors.Blue4 || '#0063FF',
        },
        [TAG_VARIANTS.YELLOW]: {
            background: {
                default: colors.Yellow0 || '#FFFBC1',
                hover: colors.Yellow1 || '#FFF48D',
                pressed: colors.Yellow2 || '#FEE352',
            },
            text: colors.Neutral11 || '#343436',
            border: colors.Blue4 || '#0063FF',
        },
    };
    return variantMap[variant] || variantMap[TAG_VARIANTS.DEFAULT];
};
const StyledTag = styled.div.withConfig({
    shouldForwardProp: (prop) =>
        !['isRemoveable', 'variant', 'appearance', 'isValid'].includes(prop),
})`
    /* Apply typography - 14px normal weight for tag labels */
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}

    display: inline-flex;
    min-width: 32px;
    padding: 0px 8px;
    justify-content: flex-start;
    align-items: center;
    gap: 4px;
    cursor: ${({ isRemoveable }) => (isRemoveable ? 'pointer' : 'default')};
    transition: background-color 0.2s, border-color 0.2s;

    /* Border radius based on appearance */
    border-radius: ${({ appearance }) =>
        appearance === TAG_APPEARANCES.ROUNDED ? '10px' : '4px'};

    /* Dynamic colors based on variant and validity */
    ${({ theme, variant, isValid }) => {
        // If invalid, always use red variant
        const effectiveVariant = isValid === false ? TAG_VARIANTS.RED : variant;
        const variantColors = getVariantColors(effectiveVariant, theme);
        return css`
            background-color: ${variantColors.background.default};
            color: ${variantColors.text};
            border: 1px solid transparent;

            /* Hover state */
            &:hover {
                background-color: ${variantColors.background.hover};
            }

            /* Active/pressed state */
            &:active {
                background-color: ${variantColors.background.pressed};
            }

            /* Focus state - only on keyboard focus (focus-visible) */
            &:focus-visible {
                outline: 2px solid ${variantColors.border};
                outline-offset: 1px;
            }
        `;
    }}

    /* Ensure minimum touch target size for removeable tags */
    ${({ isRemoveable }) =>
        isRemoveable &&
        css`
            min-height: 21px;
            min-width: 44px;
        `}
    
    /* Ensure X icon maintains consistent size and doesn't get compressed */
    svg:last-child {
        flex-shrink: 0;
        width: 16px !important;
        height: 16px !important;
    }
`;

/**
 * LytTag component - A badge/tag component for display and removal
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The content to display in the tag
 * @param {function} props.onRemoveClick - Optional click handler for tag removal (entire tag is clickable)
 * @param {string} props.leftIcon - Optional left icon name from LytIcon
 * @param {string} props.rightIcon - Optional right icon name from LytIcon
 * @param {string} props.ariaLabel - Accessible label for the tag
 * @param {string} props.variant - Color variant: 'default' | 'red' | 'green' | 'blue' | 'yellow'
 * @param {string} props.appearance - Border radius style: 'default' (squared) | 'rounded' (pill)
 * @param {boolean} props.isValid - Whether the tag is valid (false forces red variant)
 * @param {string} props.className - Additional CSS class names
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} The rendered tag component
 */
const LytTag = ({
    children,
    onRemoveClick = null,
    leftIcon = null,
    rightIcon = null,
    ariaLabel = null,
    variant = TAG_VARIANTS.DEFAULT,
    appearance = TAG_APPEARANCES.DEFAULT,
    isValid = true,
    className = '',
    testId = null,
    ...restProps
}) => {
    const handleRemoveClick = (e) => {
        e.stopPropagation(); // Prevent any potential event bubbling
        if (onRemoveClick) {
            onRemoveClick(e);
        }
    };
    const handleKeyDown = (e) => {
        // Handle keyboard navigation for removeable tags
        if (onRemoveClick && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onRemoveClick(e);
        }
    };
    const tagProps = {
        className,
        'data-testid': testId,
        'aria-label': ariaLabel,
        isRemoveable: !!onRemoveClick,
        variant,
        appearance,
        isValid,
        // Add click and keyboard handlers for removeable tags
        ...(onRemoveClick && {
            onClick: handleRemoveClick,
            onKeyDown: handleKeyDown,
            tabIndex: 0,
            role: 'button',
        }),
        ...restProps,
    };
    return (
        <StyledTag {...tagProps}>
            {leftIcon && <LytIcon name={leftIcon} size={16} />}
            <TextContent>{children}</TextContent>
            {rightIcon && <LytIcon name={rightIcon} size={16} />}
            {onRemoveClick && <LytIcon name={ICON_NAMES.X} size={16} />}
        </StyledTag>
    );
};
LytTag.propTypes = {
    children: PropTypes.node.isRequired,
    onRemoveClick: PropTypes.func,
    leftIcon: PropTypes.oneOf(Object.values(ICON_NAMES)),
    rightIcon: PropTypes.oneOf(Object.values(ICON_NAMES)),
    ariaLabel: PropTypes.string,
    variant: PropTypes.oneOf(Object.values(TAG_VARIANTS)),
    appearance: PropTypes.oneOf(Object.values(TAG_APPEARANCES)),
    isValid: PropTypes.bool,
    className: PropTypes.string,
    testId: PropTypes.string,
};
export default LytTag;
